import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Lock, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

const BRAND_IMG = "https://customer-assets-jt897jd0.emergentagent.net/job_agency-ops-hub-20/artifacts/5os5ah5c_file_00000000ab2481f78c7c869ffd3c71ed.png";

function destFor(u) {
  return u?.is_platform ? "/platform" : "/dashboard";
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate(destFor(user), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const ctx = await login(email.trim(), password);
      toast.success("Welcome back");
      navigate(destFor(ctx), { replace: true });
    } catch (err) {
      const msg = apiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full bg-slate-950">
      {/* LEFT — approved PETRINOS branding image (not cropped/distorted) */}
      <div className="hidden w-1/2 items-center justify-center bg-[#0b1a2b] lg:flex" data-testid="login-brand-panel">
        <img src={BRAND_IMG} alt="PETRINOS Travel Operating System" className="max-h-screen w-full object-contain" />
      </div>

      {/* RIGHT — login form */}
      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <Link to="/" className="mb-8 flex items-center gap-3 lg:hidden">
            <img src={BRAND_IMG} alt="PETRINOS" className="h-10 w-10 rounded object-cover" />
            <div className="font-display text-lg font-extrabold text-white">PETRINOS Travel OS</div>
          </Link>

          <h2 className="font-display text-2xl font-bold text-white">Sign in</h2>
          <p className="mt-1 text-sm text-slate-400">Access your PETRINOS Travel OS workspace</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Email</label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                  data-testid="login-email-input"
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500" />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Password</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  data-testid="login-password-input"
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500" />
              </div>
            </div>

            {error && <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs font-medium text-rose-300" data-testid="login-error">{error}</div>}

            <button type="submit" disabled={loading} data-testid="login-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-sky-500 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sky-400 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-400" data-testid="login-to-register">
            New to PETRINOS Travel OS?{" "}
            <Link to="/register" className="font-semibold text-sky-400 hover:text-sky-300">Start a free trial</Link>
          </p>
          <p className="mt-8 text-center text-[11px] text-slate-600">HelStèEn · PT Helsteen Syntra Teknologi</p>
        </div>
      </div>
    </div>
  );
}
